import { apiService } from '@/services/api/apiService';
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

interface ConvenienceFees {
  areaCode: any;
  id: any;
  packUnit: string;
}

interface ConvenienceFeesState {
  data: ConvenienceFees[];
  loading: boolean;
  error: string | null;
}

const PORT = import.meta.env.VITE_USERS_PORT;
const MASTERURL = import.meta.env.VITE_API_USERS_URL;

export const fetchConvenienceFees = createAsyncThunk<
  ConvenienceFees[],
  void,
  { rejectValue: string }
>('ConvenienceFees/fetchConvenienceFees', async (_, { rejectWithValue }) => {
  try {
    const response = await apiService.get<{ data: ConvenienceFees[] }>(
      '/get_all_area_based_platform_fee',
      PORT,
      MASTERURL,
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
  }
});

export const createConvenienceFees = createAsyncThunk<
  ConvenienceFees,
  any,
  { rejectValue: string }
>('ConvenienceFees/createConvenienceFees', async (params, { rejectWithValue }) => {
  try {
    const response = await apiService.post<{ data: ConvenienceFees }>(
      '/create_area_based_platform_fee',
      params,
      PORT,
      MASTERURL,
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
  }
});

export const updateConvenienceFees = createAsyncThunk<
  ConvenienceFees,
  any,
  { rejectValue: string }
>('ConvenienceFees/updateConvenienceFees', async (params, { rejectWithValue }) => {
  try {
    const response = await apiService.put<{ data: ConvenienceFees }>(
      '/update_area_based_platform_fee',
      params,
      PORT,
      MASTERURL,
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
  }
});
export const softDeleteConvenienceFees = createAsyncThunk<
  { id: number },
  number,
  { rejectValue: string }
>('ConvenienceFees/softDeleteConvenienceFees', async (id, { rejectWithValue }) => {
  try {
    const response = await apiService.patch<{ data: { id: number } }>(
      `/delete_area_based_platform_fee`, // ❌ remove query param
      { id }, // ✅ send JSON body
      PORT,
      MASTERURL,
    );

    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'An error occurred while deleting Convenience Fee',
    );
  }
});

export const ConvenienceFeesSlice = createSlice({
  name: 'ConvenienceFees',
  initialState: {
    data: [],
    loading: false,
    error: null,
  } as ConvenienceFeesState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchConvenienceFees.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchConvenienceFees.fulfilled,
        (state, action: PayloadAction<ConvenienceFees[]>) => {
          state.loading = false;
          state.data = action.payload;
        },
      )
      .addCase(fetchConvenienceFees.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Unknown error';
      });
  },
});

export default ConvenienceFeesSlice.reducer;
